import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { PortfolioBacktest } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import type { GlobalExitCandidateSearchResult } from '@/components/UI/exitCandidateSelectItems';

/** Server-side cap on ids per searchGlobalCandidates request. */
const INCLUDE_IDS_LIMIT = 50;

/** Matches no candidate label, so each request returns only the ids it asked for. */
const HYDRATION_QUERY = '__project314_frozen_exit_candidate_hydration__';

/**
 * Kept at module scope so `useQueries` reuses the combined map until a lookup result changes. The
 * array it returns without `combine` is new on every render, and the runs table's columns depend on
 * this map, so every keystroke in the launch form would otherwise remount every row.
 */
const combineExitCandidateLabels = (
    hydrationQueries: readonly { data?: { candidates: readonly Pick<GlobalExitCandidateSearchResult, 'id' | 'label'>[] } }[]
): ReadonlyMap<number, string> => {
    const labelMap = new Map<number, string>();

    for (const hydrationQuery of hydrationQueries) {
        for (const candidate of hydrationQuery.data?.candidates ?? []) labelMap.set(candidate.id, candidate.label);
    }

    return labelMap;
};

/**
 * Resolves the exit candidate ids frozen into the listed backtests to their display labels, so the
 * strategies-cell tooltips and expanded rows can say what `#55888` means. A failed or in-flight
 * lookup simply leaves ids out of the map — callers fall back to the bare id, which is exactly what
 * the page showed before labels existed.
 *
 * `status` is deliberately not sent: a frozen run may reference a candidate that has since been
 * retired, and the id-hydration path returns candidates regardless of status.
 */
export const useFrozenExitCandidateLabels = (backtests: readonly PortfolioBacktest[]): ReadonlyMap<number, string> => {
    const exitCandidateIdChunks = useMemo(() => {
        const uniqueIds = new Set<number>();

        for (const backtest of backtests) {
            for (const strategy of backtest.strategiesSubscribed) {
                for (const exitCandidateId of strategy.exitCandidateIds ?? []) uniqueIds.add(exitCandidateId);
            }
        }

        // Sorted so chunk contents (and therefore query cache keys) stay stable across refetches.
        const sortedIds = [...uniqueIds].sort((a, b) => a - b);
        const chunks: number[][] = [];

        for (let index = 0; index < sortedIds.length; index += INCLUDE_IDS_LIMIT) {
            chunks.push(sortedIds.slice(index, index + INCLUDE_IDS_LIMIT));
        }

        return chunks;
    }, [backtests]);

    return useQueries({
        queries: exitCandidateIdChunks.map((includeIds) => ({
            ...trpc.tradeLogicExitCandidates.searchGlobalCandidates.queryOptions({
                query: HYDRATION_QUERY,
                includeIds,
                limit: 1
            }),
            staleTime: 60000
        })),
        combine: combineExitCandidateLabels
    });
};
